import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MapPin, Calendar, Ruler, Award } from "lucide-react";

const PROJECTS = [
  {
    id: "001",
    title: "Glasfassade · Bürokomplex",
    type: "Glas- & Rahmenreinigung",
    location: "Augsburg-City",
    year: "2026",
    size: "2.400 m²",
    image:
      "https://images.unsplash.com/photo-1496307653780-42ee777d4833?auto=format&fit=crop&w=1600&q=80",
    color: "from-sky-500 to-blue-700",
    layout: "md:col-span-7 md:row-span-2",
    aspect: "aspect-[16/10]",
  },
  {
    id: "002",
    title: "Treppenhaus · Mehrfamilienhaus",
    type: "Treppenhausreinigung",
    location: "Haunstetten",
    year: "laufend",
    size: "6 Etagen",
    image:
      "https://images.pexels.com/photos/16628710/pexels-photo-16628710.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1600&w=900",
    color: "from-amber-400 to-orange-600",
    layout: "md:col-span-5 md:row-span-2",
    aspect: "aspect-[4/6]",
  },
  {
    id: "003",
    title: "Bürohaus · Grundreinigung",
    type: "Gebäudereinigung",
    location: "Lechhausen",
    year: "2025",
    size: "1.100 m²",
    image:
      "https://images.pexels.com/photos/8682781/pexels-photo-8682781.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
    color: "from-emerald-400 to-teal-600",
    layout: "md:col-span-4",
    aspect: "aspect-[4/3]",
  },
  {
    id: "004",
    title: "Zufahrt · Winterdienst",
    type: "Winterdienst",
    location: "Göggingen",
    year: "Winter 2025/26",
    size: "Saisonvertrag",
    image:
      "https://images.pexels.com/photos/6952447/pexels-photo-6952447.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
    color: "from-slate-400 to-slate-700",
    layout: "md:col-span-4",
    aspect: "aspect-[4/3]",
  },
  {
    id: "005",
    title: "Schaufenster · Einzelhandel",
    type: "Glas- & Rahmenreinigung",
    location: "Maximilianstraße",
    year: "wöchentlich",
    size: "12 Filialen",
    image:
      "https://images.pexels.com/photos/32561585/pexels-photo-32561585.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
    color: "from-cyan-400 to-sky-600",
    layout: "md:col-span-4",
    aspect: "aspect-[4/3]",
  },
];

const STATS = [
  { value: "120+", label: "Abgeschlossene Projekte" },
  { value: "6", label: "Jahre Erfahrung" },
  { value: "100%", label: "Weiterempfehlung" },
  { value: "24 h", label: "Antwortzeit" },
];

export default function Portfolio() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });
  const markerY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);

  const [hoveredId, setHoveredId] = useState(null);

  return (
    <section
      id="referenzen"
      data-testid="portfolio-section"
      ref={sectionRef}
      className="relative py-24 md:py-36 overflow-hidden bg-white text-slate-900"
      style={{ perspective: "1400px" }}
    >
      {/* Luxury ambience background */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(2,132,199,0.10),transparent_55%),radial-gradient(ellipse_at_bottom_right,rgba(245,158,11,0.08),transparent_55%)]" />
      <motion.div
        style={{ y: markerY }}
        className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 w-[1100px] h-[1100px] rounded-full bg-gradient-to-br from-sky-300/20 via-transparent to-amber-300/15 blur-3xl"
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(15,23,42,0.9) 1px,transparent 1px),linear-gradient(90deg,rgba(15,23,42,0.9) 1px,transparent 1px)",
          backgroundSize: "80px 80px",
          maskImage:
            "radial-gradient(ellipse at center, black 20%, transparent 75%)",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
        {/* Premium header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10 mb-16 md:mb-24">
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-3 mb-6 rounded-full border border-slate-200 bg-white/80 backdrop-blur px-4 py-1.5 shadow-sm"
            >
              <Award className="w-3.5 h-3.5 text-amber-500" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-700">
                Ausgewählte Referenzen
              </span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="font-display text-4xl sm:text-5xl lg:text-[64px] font-semibold tracking-tight leading-[1.02] text-slate-900"
            >
              Unsere <span className="italic font-normal text-slate-400">Arbeiten</span>
              <span className="text-sky-500">.</span>
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-slate-600 max-w-sm text-base md:text-[17px] leading-relaxed md:pb-3"
          >
            Eine kleine Auswahl unserer abgeschlossenen und laufenden Objekte —
            Projekte, auf die wir mit Sorgfalt geblickt haben.
          </motion.p>
        </div>

        {/* Metric bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-px rounded-2xl overflow-hidden border border-slate-200 bg-slate-100 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.15)] mb-14 md:mb-20"
        >
          {STATS.map((s, i) => (
            <div
              key={s.label}
              className="relative bg-white px-6 py-7 md:px-8 md:py-8 group"
            >
              <div className="font-display text-4xl md:text-5xl font-semibold tracking-tight bg-gradient-to-br from-slate-900 via-slate-800 to-sky-600 bg-clip-text text-transparent">
                {s.value}
              </div>
              <div className="mt-2 text-xs uppercase tracking-widest text-slate-500 font-medium">
                {s.label}
              </div>
              {i < STATS.length - 1 && (
                <div className="hidden md:block absolute right-0 top-6 bottom-6 w-px bg-gradient-to-b from-transparent via-slate-200 to-transparent" />
              )}
            </div>
          ))}
        </motion.div>

        {/* Asymmetric bento gallery */}
        <div className="grid grid-cols-1 md:grid-cols-12 auto-rows-[220px] md:auto-rows-[260px] gap-4 md:gap-5">
          {PROJECTS.map((p, i) => (
            <motion.article
              key={p.id}
              data-testid={`project-${p.id}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              onMouseEnter={() => setHoveredId(p.id)}
              onMouseLeave={() => setHoveredId(null)}
              className={`group relative overflow-hidden rounded-3xl border border-slate-200 bg-white cursor-pointer shadow-[0_10px_40px_-20px_rgba(15,23,42,0.15)] hover:shadow-[0_30px_70px_-20px_rgba(2,132,199,0.35)] transition-shadow duration-500 ${p.layout}`}
              style={{ transform: "translateZ(0)" }}
            >
              {/* Image */}
              <div className="absolute inset-0">
                <img
                  src={p.image}
                  alt={p.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.08]"
                  loading="lazy"
                />
                {/* Dynamic overlay */}
                <div
                  className={`absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/30 to-transparent transition-opacity duration-700 ${
                    hoveredId === p.id ? "opacity-85" : "opacity-100"
                  }`}
                />
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${p.color} mix-blend-soft-light opacity-30 group-hover:opacity-60 transition-opacity duration-700`}
                />
              </div>

              {/* Shine sweep on hover */}
              <div
                className="pointer-events-none absolute -inset-1 translate-x-[-120%] group-hover:translate-x-[120%] transition-transform duration-[1600ms] ease-out"
                style={{
                  background:
                    "linear-gradient(115deg, transparent 42%, rgba(255,255,255,0.18) 50%, transparent 58%)",
                }}
              />

              {/* Top meta row */}
              <div className="absolute top-5 left-5 right-5 flex items-start justify-between gap-3">
                <span className="inline-flex items-center gap-2 rounded-full bg-black/40 backdrop-blur-md border border-white/15 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/90">
                  <span className={`w-1.5 h-1.5 rounded-full bg-gradient-to-br ${p.color}`} />
                  {p.type}
                </span>
                <span className="font-display text-sm font-semibold tracking-wider text-white/70 pt-1.5">
                  /{p.id}
                </span>
              </div>

              {/* Hover-to-open indicator */}
              <div className="absolute top-5 right-5 mt-10 opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-500">
                <div className="w-11 h-11 rounded-full bg-white text-slate-900 grid place-items-center shadow-2xl">
                  <ArrowUpRight className="w-5 h-5" strokeWidth={2.4} />
                </div>
              </div>

              {/* Bottom content */}
              <div className="absolute left-5 right-5 bottom-5 md:left-6 md:right-6 md:bottom-6">
                <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.18em] text-white/60 mb-2">
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="w-3 h-3" /> {p.location}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-white/30" />
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar className="w-3 h-3" /> {p.year}
                  </span>
                </div>
                <h3 className="font-display text-xl md:text-2xl lg:text-[28px] font-semibold tracking-tight leading-tight text-white">
                  {p.title}
                </h3>
                <div
                  className="mt-3 overflow-hidden transition-all duration-500 ease-out max-h-0 group-hover:max-h-[60px]"
                >
                  <div className="flex items-center gap-4 text-sm text-white/80 pt-1">
                    <span className="inline-flex items-center gap-1.5">
                      <Ruler className="w-3.5 h-3.5" /> {p.size}
                    </span>
                    <span className="w-1 h-1 rounded-full bg-white/30" />
                    <span className="font-semibold text-white">Details ansehen</span>
                  </div>
                </div>
              </div>

              {/* Corner accent line */}
              <div className={`pointer-events-none absolute bottom-0 left-0 h-[3px] bg-gradient-to-r ${p.color} w-0 group-hover:w-full transition-all duration-700 ease-out`} />
            </motion.article>
          ))}
        </div>

        {/* Premium CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-14 md:mt-16 flex flex-col md:flex-row items-center justify-between gap-6"
        >
          <p className="font-display text-lg md:text-xl text-slate-700 text-center md:text-left">
            Werden auch Sie Teil unserer <span className="text-sky-600 font-semibold">Referenzliste</span>.
          </p>
          <a
            href="#kontakt"
            data-testid="portfolio-cta"
            className="group inline-flex items-center gap-3 rounded-full bg-slate-900 text-white px-6 py-3.5 text-sm font-semibold shadow-[0_20px_60px_-20px_rgba(15,23,42,0.5)] transition-transform hover:-translate-y-0.5 hover:bg-sky-600"
          >
            Projekt anfragen
            <span className="grid place-items-center w-8 h-8 rounded-full bg-white text-slate-900 transition-all group-hover:rotate-45">
              <ArrowUpRight className="w-4 h-4" strokeWidth={2.4} />
            </span>
          </a>
        </motion.div>
      </div>
    </section>
  );
}
